/**
 * A collapsible Help section with a copy-link button. Shared by the Skills & Plugins and
 * MCP Servers Help topics (#939, Feature G).
 */
import { useEffect, useRef, useState } from "react";
import CheckIcon from "../icons/CheckIcon";
import LinkIcon from "../icons/LinkIcon";
import { CLAUDE_APP_LABEL } from "../lib/install-instructions";
import { routeToHash, type HelpSectionId } from "../lib/routing";
import styles from "./HelpPage.module.css";

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      className={[styles.chevron, expanded && styles.chevronOpen].filter(Boolean).join(" ")}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// Shorter than the titles so the link button and toggle don't share an accessible name.
const SECTION_LINK_LABELS: Record<HelpSectionId, string> = {
  "chat-cowork": CLAUDE_APP_LABEL,
  "claude-code": "Claude Code",
  "usage-figures": "Usage figures",
  "contributing": "Contributing",
  "mcp-what": "What's an MCP server",
  "mcp-desktop": "MCP in Claude Desktop",
  "mcp-tool-permissions": "Tool permissions",
  "mcp-cowork": "MCP in Cowork",
  "mcp-claude-code": "MCP in Claude Code",
  "mcp-other-clients": "Other MCP clients",
  "mcp-first-use": "First use of a server",
  "mcp-managing": "Managing access",
  "mcp-troubleshooting": "MCP troubleshooting",
  "mcp-request": "Request a server",
};

function SectionLinkButton({ id }: { id: HelpSectionId }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const label = SECTION_LINK_LABELS[id];

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleCopy = () => {
    if (!navigator.clipboard) return;
    const url = `${window.location.origin}${window.location.pathname}${routeToHash({ page: "help", section: id })}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? `Copied link to ${label} section` : `Copy link to ${label} section`}
      className={[styles.sectionLinkButton, copied && styles.sectionLinkButtonCopied].filter(Boolean).join(" ")}
    >
      {copied ? <CheckIcon width="14" height="14" /> : <LinkIcon width="14" height="14" />}
    </button>
  );
}

export function Section({
  id, title, defaultOpen, onOpen, children,
}: {
  id: HelpSectionId; title: string; defaultOpen?: boolean; onOpen?: (id: HelpSectionId) => void; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) onOpen?.(id);
  };
  return (
    <div id={id} className={styles.section}>
      <div className={[styles.sectionHeader, open && styles.sectionHeaderOpen].filter(Boolean).join(" ")}>
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={open}
          className={styles.sectionToggle}
        >
          <span className={styles.sectionTitle}>{title}</span>
          <ChevronIcon expanded={open} />
        </button>
        <SectionLinkButton id={id} />
      </div>
      {open && (
        <div className={styles.sectionContent}>
          {children}
        </div>
      )}
    </div>
  );
}
